import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Building2, Mailbox, MapPin, IdCard, House } from "lucide-react";
import { estadoServicio } from "../../../../../functions/funciones";
import DataTable from "../../../../../components/Tables/DataTable";
import ViewModeToggle from "../../../../../components/Tables/ViewModeToggle";
import FiltrosComunidadesLegalia from "../../../../../components/filtros/FiltrosComunidadesLegalia";
import Empty from "../../../../../icons/empty.png";

const statusStyles = {
  1: "bg-green-100 text-green-700 border-none",       // Activa
  2: "bg-orange-100 text-orange-600 border-none",      // Pdte Renovación
  3: "bg-red-100 text-red-600 border-none",            // Baja vto
  4: "bg-red-100 text-red-600 border-none",            // Baja
  5: "bg-red-200 text-red-700 border-none",            // Anulado
};
const ScoreCircle = ({ score, size = 66 }) => {
  const target = Math.min(Math.max(score || 0, 0), 100);
  const color =
    target === 0
      ? "#ef4444"
      : target <= 50
      ? "#eab308"
      : "#22c55e";

  const center = size / 2;
  const strokeWidth = size >= 80 ? 4 : 3;
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const targetDash = (target / 100) * circumference;
  const fontSize = Math.max(10, Math.round(size * 0.24));

  const [dash, setDash] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const start = performance.now();
    const duration = 800;
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = ease(p);
      setDash(targetDash * eased);
      setDisplayScore(Math.round(target * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, targetDash]);

  return (
    <span className="inline-flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight="700"
          fill={color}
        >
          {displayScore}%
        </text>
      </svg>
    </span>
  );
};

const Badge = ({ statusId, label, className = "" }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${
      statusStyles[statusId] ?? "bg-gray-100 text-gray-500"
    } ${className}`}
  >
    {label}
  </span>
);

function ComunidadesLegalia({
  t,
  user,
  data,
  loading,
  fetchData,
  busqueda,
  filtrarDatos,
  busquedaActiva,
  setBusquedaActiva,
  clearFilterActivo,
  setClearFilterActivo,
}) {
  const meta = data.meta || {};
  const rows = (data.rows || []).filter(row => !row.isWorkcenter);

  // ¿Alguna comunidad cargada tiene contratado el servicio (estado 1, 2 o 3)?
  const tieneComunidadCai = meta.serviceCounts?.cai > 0 ? true : false
  const tieneComunidadKae = meta.serviceCounts?.kae > 0 ? true : false
  const [viewMode, setViewMode] = useState("cards");

  const comunidadesRows = [
    {
      id: "razonSocial",
      label: t('columns.community'),
      overrideFunc: (data, row) => (
        <>
          <Link to={`/comunidades/${row.nif}`} className="font-medium text-gray-900 hover:underline">
            {row.razonSocial}
          </Link>
          <div className="text-xs text-gray-400 mt-0.5">
            {row.municipio}, {row.codigoPostal}
          </div>
        </>
      ),
    },
    ...(user.parent === true ? [{
      id: "aaff_razonSocial",
      label: t('aaff:fields.office').toUpperCase(),
    }] : []),
    {
      id: "nif",
      label: t('columns.nif'),
    },
    {
      id: "estadoCae",
      label: t('columns.contractedCae'),
      overrideFunc: (data, row) =>
        !row.isWorkcenter && data !== "-" ? (
          <Badge statusId={data} label={estadoServicio(data, t)} className="ml-6" />
        ) : data === "-" ? <span className="ml-6">-</span> : "",
    },
    {
      id: "porcentajeDocs",
      label: t('columns.coverageCae'),
      overrideFunc: (data, row) =>
        !row.isWorkcenter ? (
          <span className="inline-flex ml-6">
            <ScoreCircle score={Math.round(data || 0)} />
          </span>
        ) : "",
    },
    {
      id: "estadoLopd",
      label: t('columns.contractedLopd'),
      overrideFunc: (data, row) =>
        !row.isWorkcenter && data !== "-" ? (
          <Badge statusId={data} label={estadoServicio(data, t)} />
        ) : data === "-" ? "-" : "",
    },
  ];

  const renderComunidadCard = (row, index = 0) => {
    const hasCae = row.estadoCae && row.estadoCae !== "-";
    const hasLopd = row.estadoLopd && row.estadoLopd !== "-";

    return (
      <div
        style={{
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(229, 231, 235, 0.55) 100%)",
          borderRadius: 14,
          padding: "18px 20px",
          border: "1px solid rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 3px 12px rgba(17, 24, 39, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
          height: "100%",
          animationDelay: `${Math.min(index, 12) * 40}ms`,
        }}
        className="flex flex-col legalia-card-enter"
      >
        {/* Header: nombre + NIF + datos */}
        <div>
          <Link
            to={`/comunidades/${row.nif}`}
            className="font-semibold text-gray-900 hover:underline text-base block break-words"
          >
            <House className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.razonSocial}
          </Link>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <IdCard className="text-[#3651d8] mr-2 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.nif || "-"}
          </div>
          <div className="text-xs text-gray-900 mt-1 truncate">
            <MapPin className="text-[#3651d8] mr-1 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.municipio && <> {row.municipio}</>}
          </div>
          <div className="text-xs text-gray-900 mt-1 truncate ">
            <Mailbox className="text-[#3651d8] mr-1 mb-1 w-5 h-5" strokeWidth={1.5} />
            {row.codigoPostal && <> {row.codigoPostal}</>}
          </div>
        </div>

        {/* Bloque inferior: porcentaje + 2 columnas + oficina (anclado abajo, alineado entre cards) */}
        <div className="mt-auto">
          {/* Porcentaje grande centrado */}
          <div className="flex flex-col items-center justify-center py-2 border-y border-gray-100 my-3">
            <ScoreCircle score={Math.round(row.porcentajeDocs || 0)} size={110} />
            <div className="text-[11px] text-gray-900 mt-1 uppercase tracking-wide">
              {t('columns.coverageCae')}
            </div>
          </div>

          {/* 2 columnas: CAE | LOPD */}
          <div className="flex justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-900 uppercase tracking-wide mb-1">
                {t('columns.contractedCae')}
              </div>
              {hasCae
                ? <Badge statusId={row.estadoCae} label={estadoServicio(row.estadoCae, t)} />
                : <span className="text-sm text-gray-400">-</span>}
            </div>

            <div className="flex-1 min-w-0 text-right">
              <div className="text-[10px] text-gray-900 uppercase tracking-wide mb-1">
                {t('columns.contractedLopd')}
              </div>
              {hasLopd
                ? <Badge statusId={row.estadoLopd} label={estadoServicio(row.estadoLopd, t)} />
                : <span className="text-sm text-gray-400">-</span>}
            </div>
          </div>

          {/* Oficina (siempre renderiza si parent, con placeholder si no hay dato — mantiene alineación) */}
          {user.parent === true && (
            <div className="flex items-center gap-2 text-xs text-gray-900 mt-3 pt-3 border-t border-gray-100">
              <Building2 className="w-3.5 h-3.5 shrink-0 text-gray-400" />
              <span className="truncate">{row.aaff_razonSocial || "-"}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const mensaje=(
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <img src={Empty} alt="Empty" style={{ width: 75 }} />
      <p className="text-base font-semibold text-gray-900">{t('noCommunities')}</p>
      <p className="text-sm text-gray-900 text-center max-w-xs">
        {t('noCommunities2')}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans overflow-x-hidden">
      <div className="max-w-8xl mx-auto bg-white shadow-sm p-3 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

          {/* Toolbar */}
        <FiltrosComunidadesLegalia
            onFilter={filtrarDatos}
            filtrosIniciales={busqueda}
            user={user}
            tieneComunidadCai={tieneComunidadCai}
            tieneComunidadKae={tieneComunidadKae}
        />

        <DataTable
          variant="legalia"
          viewMode={viewMode}
          renderCard={renderComunidadCard}
          data={rows}
          meta={meta}
          headers={comunidadesRows}
          isLoading={loading}
          fetcher={fetchData}
          filtros={busqueda}
          busquedaActiva={busquedaActiva}
          setBusquedaActiva={setBusquedaActiva}
          clearFilterActivo={clearFilterActivo}
          setClearFilterActivo={setClearFilterActivo}
          mensaje={mensaje}
        />
      </div>
    </div>
  );
}

export default ComunidadesLegalia;
